import { Css, Palette, useTestIds } from "@homebound/beam";
import { BarDatum } from "@nivo/bar";
import { useMemo } from "react";
import { BarChart } from "src/components/charts/BarChart";
import {
  BudgetOverviewTab_ProjectFragment,
  BudgetOverviewTab_SellableProjectFragment,
} from "src/routes/projects/budget/components/reports/BudgetOverviewTab.generated";
type AverageVarianceWidgetProps = { project: BudgetOverviewTab_ProjectFragment };

export function AverageVarianceWidget({ project }: AverageVarianceWidgetProps) {
  const tid = useTestIds({});
  const { chartData, averageVariance } = useMemo(() => mapToChartData(project.sellableProjects), [project]);
  // Get y-axis ticks based on the chart data
  const yTicks = useMemo(() => getYChartTicks(chartData), [chartData]);

  return (
    <div css={Css.w100.mw0.hPx(320).p3.bgWhite.ba.bcGray300.bw2.br8.$}>
      <div {...tid.percentageLabel} css={Css.tal.xl2.pb1.$}>
        {averageVariance}%
      </div>
      <div css={Css.lg.gray700.pb5.df.$}>Average Variance</div>
      <div css={Css.h("55%").w100.$} {...tid.BarChart}>
        <BarChart
          dataset={chartData.isEmpty ? emptyDataset : chartData}
          keys={chartData.isEmpty ? ["empty_data"] : chartData.map((v) => v.toKeys().first!)}
          colors={
            chartData.isEmpty
              ? [Palette.Transparent]
              : [Palette.Blue600, ...Array.from({ length: 9 }, () => Palette.Blue300)]
          }
          emptyStateTooltipText={chartData.isEmpty ? "There are no projects that have started yet." : undefined}
          tooltipValueFormat={(object) =>
            `${object.data.name} has a ${object.formattedValue}% variance from its starting budget`
          }
          margin={{ top: 5, bottom: 5 }}
          padding={0.2}
          disabled={chartData.isEmpty}
          labelSkipHeight={30}
          label={(d) => `${d.value}%`}
          labelTextColor={Palette.White}
          enableLabel={chartData.nonEmpty}
          gridYValues={yTicks}
          axisLeft={{ tickValues: [] }}
          axisBottom={{ tickValues: [] }}
        />
      </div>
    </div>
  );
}

function mapToChartData(data: BudgetOverviewTab_SellableProjectFragment[]): {
  chartData: BarDatum[];
  averageVariance: string;
} {
  // Sort by project variance
  const sortedProjects = data
    .filter((p) => !!p.directCostAtStartInCents)
    .map((p) => ({ ...p, variance: getVariancePercentage(p) }))
    .sortBy((p) => p.variance)
    .reverse();
  // Map to chart data, use the first 10 projects
  const chartData = sortedProjects
    .slice(0, 10)
    .map((p) => ({ [p.id]: Math.round(p.variance), name: p.name, id: p.id }));
  return {
    chartData,
    averageVariance: sortedProjects.nonEmpty
      ? (sortedProjects.sum((p) => p.variance) / sortedProjects.length).toFixed(2)
      : "--",
  };
}

function getVariancePercentage(project: BudgetOverviewTab_SellableProjectFragment) {
  const { directCostVarianceInCents: costVariance, directCostAtStartInCents: costAtStart } = project;
  return costVariance && costAtStart ? (costVariance / costAtStart) * 100 : 0;
}

function getYChartTicks(data: BarDatum[]): number[] {
  if (data.isEmpty) return [0, 33, 66, 100]; // Fallback for empty data
  const yValues = data.map((d) => d.toValues().first! as number);
  const yMin = 0;
  const yMax = Math.max(...yValues);
  return yMin === yMax ? [yMin] : Array.from({ length: 4 }, (_, i) => Math.round(yMin + ((yMax - yMin) / 3) * i));
}

const emptyDataset = [{ empty_data: 100 }];
